import { useEffect, useState } from 'react';

import { getRefBehaviors } from 'api/dataCollection';

/**
 * Every interval recording on one behavior-interval sheet, newest first.
 *
 * A therapy session can hold many interval recordings (one per behaviour) in
 * raw_data.sessions[]; the flat top-level fields mirror the latest one. Older
 * records have only the flat fields. When the sheet was auto-saved mid-recording,
 * the flat fields hold a recording not yet in sessions[] - it is listed first and
 * flagged `_inProgress`. Stale copies of other sessions' recordings (`_historical`)
 * are left out.
 */
export const intervalRecordings = (raw) => {
  if (!raw) return [];
  // The flat recording's observer notes: sync moves them out of raw_data into the
  // record's notes column (viewers fold it back in as raw.notes).
  const flatNotes = raw.sessionNotes ?? raw.notes ?? '';
  // Items flagged _historical are stale copies of OTHER sessions' recordings that
  // older saves embedded in every row - not part of this session.
  const list = Array.isArray(raw.sessions) ? raw.sessions.filter((r) => r && !r._historical) : [];
  const flatHasData = Array.isArray(raw.data) && raw.data.length > 0;
  if (list.length === 0) return flatHasData || raw.behavior ? [{ ...raw, notes: flatNotes }] : [];
  const sameAsFlat = (r) => r.behavior === raw.behavior && JSON.stringify(r.data || []) === JSON.stringify(raw.data || []);
  if (flatHasData && !list.some(sameAsFlat)) {
    return [{ ...raw, sessions: undefined, notes: flatNotes, _inProgress: true }, ...list];
  }
  return list;
};

/** IEP behaviours carry no id, so sheets store a derived "iep-behavior-0-crying": show "Crying". */
export const readableBehavior = (value) =>
  String(value || '')
    .replace(/^iep-behavior-\d+-/, '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A recording's behaviour as a person reads it. Sheets store the behaviour's VALUE:
 * a derived "iep-behavior-0-crying" for IEP behaviours, but the raw id of an org-wide
 * reference behaviour. Prefer the name saved with the recording (`behaviorLabel`,
 * saved since it was added), then the reference list (`names`: id -> name, from
 * useBehaviorNames), then the readable form of the value. Never shows a bare id.
 */
export const behaviorDisplayName = (value, label, names) => {
  if (label && label !== value) return label;
  if (!value) return '';
  const key = String(value);
  if (names && names[key]) return names[key];
  return UUID_RE.test(key) ? '' : readableBehavior(key);
};

// Reference behaviours (id -> name), fetched once per page load and shared.
let refNamesCache = null;
let refNamesPromise = null;
export const loadRefBehaviorNames = () => {
  if (refNamesCache) return Promise.resolve(refNamesCache);
  if (!refNamesPromise) {
    refNamesPromise = getRefBehaviors()
      .then((list) => {
        refNamesCache = Object.fromEntries((Array.isArray(list) ? list : []).map((b) => [String(b.id), b.name]));
        return refNamesCache;
      })
      .catch(() => {
        // Offline / failed: let the next viewer retry; names fall back meanwhile.
        refNamesPromise = null;
        return {};
      });
  }
  return refNamesPromise;
};

/** id -> name map of reference behaviours, for viewers of older recordings that
 * stored only the behaviour's id. {} until loaded (and when offline). */
export const useBehaviorNames = () => {
  const [names, setNames] = useState(refNamesCache || {});
  useEffect(() => {
    let alive = true;
    loadRefBehaviorNames().then((n) => alive && setNames(n));
    return () => {
      alive = false;
    };
  }, []);
  return names;
};

/** W / P / N / missed tallies and percentages for one recording's grid - the same
 * figures BehaviorIntervalMode computes while recording. */
export const recomputeIntervalTotals = (data) => {
  const list = Array.isArray(data) ? data : [];
  const total = list.length;
  const count = (v) => list.filter((x) => x === v).length;
  const pct = (c) => (total > 0 ? Math.round((c / total) * 100) : 0);
  const wholeCount = count('whole');
  const partialCount = count('part');
  const noneCount = count('none');
  const missedCount = count('missed');
  return {
    totalIntervals: total,
    totalRecorded: list.filter((v) => v !== null && v !== undefined && v !== 'missed').length,
    wholeCount,
    partialCount,
    noneCount,
    missedCount,
    wholePercent: pct(wholeCount),
    partialPercent: pct(partialCount),
    nonePercent: pct(noneCount),
    missedPercent: pct(missedCount)
  };
};

const sameRecording = (a, b) =>
  Boolean(a && b) && a.behavior === b.behavior && JSON.stringify(a.data || []) === JSON.stringify(b.data || []);

/** The recordings a supervisor may correct - exactly what View Data lists
 * (intervalRecordings: this session's own recordings, including one auto-saved
 * mid-recording), else the flat record itself. */
export const editableIntervalRecordings = (raw) => {
  const list = intervalRecordings(raw);
  return list.length > 0 ? list : [raw || {}];
};

/** Index (into editableIntervalRecordings) of the recording the flat fields mirror. */
export const flatRecordingIndex = (raw, recordings) => recordings.findIndex((r) => r === raw || sameRecording(r, raw));

/** Other-behaviour fields after a correction: the (possibly new) target behaviour is
 * never also an "other" behaviour, and the counts follow the per-interval lists. */
const correctedOtherBehaviors = (rec, behavior) => {
  if (!Array.isArray(rec.otherBehaviors)) return {};
  const otherBehaviors = rec.otherBehaviors.map((cell) => (Array.isArray(cell) ? cell.filter((v) => v && v !== behavior) : []));
  const otherBehaviorCounts = {};
  otherBehaviors.forEach((cell) =>
    cell.forEach((v) => {
      otherBehaviorCounts[v] = (otherBehaviorCounts[v] || 0) + 1;
    })
  );
  const labels = rec.otherBehaviorLabels || {};
  const otherBehaviorLabels = Object.fromEntries(Object.keys(otherBehaviorCounts).map((v) => [v, labels[v] || readableBehavior(v)]));
  return { otherBehaviors, otherBehaviorCounts, otherBehaviorLabels };
};

/**
 * The raw_data a supervisor's correction saves. `edits` is { [index]: { behavior,
 * data, notes } } keyed by editableIntervalRecordings(raw). Every edited recording
 * gets its new behaviour / grid / notes and recomputed totals; untouched recordings
 * (and stale copies) are written back exactly as they were; the flat top-level
 * fields follow the recording they mirror. Interval type / length are never changed.
 */
export const applyIntervalCorrections = (raw, edits) => {
  const recordings = editableIntervalRecordings(raw);
  const corrected = (r, e) => ({
    ...r,
    behavior: e.behavior,
    data: [...e.data],
    notes: e.notes,
    ...recomputeIntervalTotals(e.data),
    ...correctedOtherBehaviors(r, e.behavior)
  });
  const rd = { ...raw };
  if (Array.isArray(raw.sessions) && raw.sessions.length > 0) {
    rd.sessions = raw.sessions.map((r) => {
      const i = recordings.indexOf(r);
      return i >= 0 && edits[i] ? corrected(r, edits[i]) : r;
    });
  }
  const flatIdx = flatRecordingIndex(raw, recordings);
  if (flatIdx >= 0 && edits[flatIdx]) {
    const e = edits[flatIdx];
    Object.assign(rd, {
      behavior: e.behavior,
      data: [...e.data],
      sessionNotes: e.notes,
      ...recomputeIntervalTotals(e.data),
      ...correctedOtherBehaviors(raw, e.behavior)
    });
  }
  delete rd.notes; // the notes COLUMN is folded in for display; not part of the sheet payload
  return rd;
};

/**
 * The data-collection rows View Data shows for a session, one card each.
 * One row per sheet type - the LATEST: collected latest, then SAVED latest (rows of
 * one session usually share collected_at, and a tie used to keep the OLDEST save).
 * Exempt, every row kept: injury reports (a session can have several incidents) and
 * behaviour-interval sheets, whose rows each hold different recordings (a re-opened
 * sheet saves a new row). Card order is the API order; interval rows among
 * themselves read oldest-first, in the order the recordings were made.
 */
export const pickSessionRows = (items) => {
  const savedAt = (dc) => new Date(dc.updated_at || dc.created_at || dc.collected_at || 0).getTime();
  const isNewer = (a, b) => {
    const ca = new Date(a.collected_at || 0).getTime();
    const cb = new Date(b.collected_at || 0).getTime();
    return ca !== cb ? ca > cb : savedAt(a) > savedAt(b);
  };
  const formTypeOf = (dc) => dc.raw_data?.formType || dc.datasheet_type?.name?.toLowerCase();
  const latestByType = new Map();
  (items || []).forEach((dc) => {
    const keepAll = formTypeOf(dc) === 'injury-report' || formTypeOf(dc) === 'behavior-interval';
    const typeKey = keepAll ? dc.id : dc.datasheet_type_id || dc.datasheet_type?.id || dc.raw_data?.formType || dc.id;
    const existing = latestByType.get(typeKey);
    if (!existing || isNewer(dc, existing)) latestByType.set(typeKey, dc);
  });
  const kept = Array.from(latestByType.values());
  const isInterval = (dc) => formTypeOf(dc) === 'behavior-interval';
  const intervalRows = kept.filter(isInterval).sort((a, b) => savedAt(a) - savedAt(b));
  let next = 0;
  return kept.map((dc) => (isInterval(dc) ? intervalRows[next++] : dc));
};

/**
 * The recordings a behaviour-interval sheet SAVES: the ones made in THIS therapy
 * session - new ones (no `_historical`), plus earlier ones of the same session the
 * sheet loaded back (re-opened sheet). Other days' recordings, and stale copies
 * embedded by older saves, are never re-saved. Saved recordings carry their
 * session id and no `_historical` flag.
 */
export const ownIntervalRecordings = (list, sessionId) =>
  (list || [])
    .filter((rec) => rec && (!rec._historical || (sessionId && rec._sessionId === String(sessionId))))
    .map(({ _historical, ...rest }) => ({ ...rest, _sessionId: sessionId ? String(sessionId) : rest._sessionId }));

export default intervalRecordings;
